import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Edit2, Save, X, GripVertical, CheckSquare, ShieldCheck, Calendar } from 'lucide-react';
import { CustomerCustomTodo } from '../../../shared/types';
import { customerTodoService } from '../../../services/customer/CustomerTodoService';

interface CustomTodosManagerProps {
  customerId: string;
  onUpdate: () => void;
}

// Komponente für Monatsauswahl
const MonthSelector: React.FC<{
  selectedMonths: number[] | null;
  onChange: (months: number[] | null) => void;
}> = ({ selectedMonths, onChange }) => {
  const months = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  const isAllMonths = selectedMonths === null || selectedMonths.length === 0;

  const handleMonthToggle = (monthNumber: number) => {
    if (isAllMonths) {
      // Wenn alle Monate aktiv sind, starte mit nur diesem Monat
      onChange([monthNumber]);
    } else {
      const currentMonths = selectedMonths || [];
      if (currentMonths.includes(monthNumber)) {
        const newMonths = currentMonths.filter(m => m !== monthNumber);
        // Wenn keine Monate mehr ausgewählt, setze auf null (alle Monate)
        onChange(newMonths.length > 0 ? newMonths : null);
      } else {
        onChange([...currentMonths, monthNumber]);
      }
    }
  };

  const handleAllMonthsToggle = () => {
    onChange(isAllMonths ? [1] : null); // Toggle zwischen allen und nur Januar
  };

  return (
    <div style={{ marginTop: '12px' }}>
      <label style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px',
        marginBottom: '8px',
        fontSize: '13px',
        color: '#6B7280'
      }}>
        <input
          type="checkbox"
          checked={isAllMonths}
          onChange={handleAllMonthsToggle}
        />
        In allen Monaten anzeigen
      </label>
      
      {!isAllMonths && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, 1fr)', 
          gap: '8px',
          padding: '12px',
          backgroundColor: '#F9FAFB',
          borderRadius: '6px',
          border: '1px solid #E5E7EB'
        }}>
          {months.map((month, index) => (
            <label 
              key={index}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '4px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              <input
                type="checkbox"
                checked={(selectedMonths || []).includes(index + 1)}
                onChange={() => handleMonthToggle(index + 1)}
                style={{ width: '14px', height: '14px' }}
              />
              {month}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export const CustomTodosManager: React.FC<CustomTodosManagerProps> = ({
  customerId,
  onUpdate
}) => {
  const [todos, setTodos] = useState<CustomerCustomTodo[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    title: string;
    description: string;
    requiresConfirmation: boolean;
    activeMonths: number[] | null;
  }>({
    title: '',
    description: '',
    requiresConfirmation: false,
    activeMonths: null
  });
  const [isAdding, setIsAdding] = useState(false);
  const [newForm, setNewForm] = useState<{
    title: string;
    description: string;
    requiresConfirmation: boolean;
    activeMonths: number[] | null;
  }>({
    title: '',
    description: '',
    requiresConfirmation: false,
    activeMonths: null
  });

  const loadTodos = useCallback(async () => {
    setLoading(true);
    const data = await customerTodoService.getCustomerTodos(customerId);
    setTodos(data);
    setLoading(false);
  }, [customerId]);

  useEffect(() => {
    loadTodos();
  }, [loadTodos]);

  const handleAdd = async () => {
    if (!newForm.title.trim()) return;

    const newTodo = await customerTodoService.createTodo(
      customerId,
      newForm.title.trim(),
      newForm.description.trim() || undefined,
      newForm.requiresConfirmation,
      newForm.activeMonths
    );

    if (newTodo) {
      await loadTodos();
      setNewForm({
        title: '',
        description: '',
        requiresConfirmation: false,
        activeMonths: null
      });
      setIsAdding(false);
      onUpdate();
    }
  };

  const handleEdit = (todo: CustomerCustomTodo) => {
    setEditingId(todo.id);
    setEditForm({
      title: todo.title,
      description: todo.description || '',
      requiresConfirmation: todo.requires_confirmation || false,
      activeMonths: todo.active_months || null
    });
  };

  const handleSaveEdit = async (todoId: string) => {
    const success = await customerTodoService.updateTodo(todoId, {
      title: editForm.title,
      description: editForm.description || undefined,
      requires_confirmation: editForm.requiresConfirmation,
      active_months: editForm.activeMonths
    });

    if (success) {
      await loadTodos();
      setEditingId(null);
      onUpdate();
    }
  };

  const handleDelete = async (todoId: string) => {
    if (!window.confirm('Möchten Sie dieses To-Do wirklich löschen?')) return;

    const success = await customerTodoService.deleteTodo(todoId);
    if (success) {
      await loadTodos();
      onUpdate();
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const newOrder = [...todos];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    const success = await customerTodoService.reorderTodos(
      customerId,
      newOrder.map(t => t.id)
    );
    if (success) {
      setTodos(newOrder);
      onUpdate();
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === todos.length - 1) return;
    const newOrder = [...todos];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    const success = await customerTodoService.reorderTodos(
      customerId,
      newOrder.map(t => t.id)
    );
    if (success) {
      setTodos(newOrder);
      onUpdate();
    }
  };

  // Helper-Funktion für Monats-Anzeige
  const getMonthsDisplay = (activeMonths: number[] | null): string => {
    if (!activeMonths || activeMonths.length === 0) return 'Alle Monate';
    
    const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 
                       'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    
    if (activeMonths.length === 12) return 'Alle Monate';
    if (activeMonths.length > 6) {
      return `${activeMonths.length} Monate`;
    }
    
    return activeMonths
      .sort((a, b) => a - b)
      .map(m => monthNames[m - 1])
      .join(', ');
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Lade To-Dos...</div>;
  }

  return (
    <div>
      {/* Header mit Add Button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
            Individuelle To-Dos
          </h3>
          <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6B7280' }}>
            Diese To-Dos werden bei jedem Upload für diesen Kunden angezeigt
          </p>
        </div>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              backgroundColor: '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <Plus size={16} />
            To-Do hinzufügen
          </button>
        )}
      </div>

      {/* Add Form */}
      {isAdding && (
        <div style={{
          padding: '16px',
          backgroundColor: '#F9FAFB',
          borderRadius: '8px',
          marginBottom: '16px',
          border: '1px solid #E5E7EB'
        }}>
          <div style={{ display: 'grid', gap: '12px' }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '4px', 
                fontSize: '14px', 
                fontWeight: '500' 
              }}>
                Titel *
              </label>
              <input
                type="text"
                value={newForm.title}
                onChange={(e) => setNewForm({ ...newForm, title: e.target.value })}
                placeholder="z.B. Stundenzettel prüfen"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '4px', 
                fontSize: '14px', 
                fontWeight: '500' 
              }}>
                Beschreibung
              </label>
              <textarea
                value={newForm.description}
                onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
                placeholder="Optionale detaillierte Beschreibung"
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Neue Option: Bestätigung erforderlich */}
            <div>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                fontSize: '14px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={newForm.requiresConfirmation}
                  onChange={(e) => setNewForm({ ...newForm, requiresConfirmation: e.target.checked })}
                  style={{ width: '16px', height: '16px' }}
                />
                <ShieldCheck size={16} color={newForm.requiresConfirmation ? '#10B981' : '#9CA3AF'} />
                Bestätigung vor Finalisierung erforderlich
              </label>
              <p style={{ 
                margin: '4px 0 0 28px', 
                fontSize: '12px', 
                color: '#6B7280' 
              }}>
                Wenn aktiviert, muss dieses To-Do explizit bestätigt werden, bevor die E-Mail versendet werden kann
              </p>
            </div>

            {/* Neue Option: Monatsauswahl */}
            <div>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                <Calendar size={16} color="#6B7280" />
                Aktive Monate
              </label>
              <MonthSelector 
                selectedMonths={newForm.activeMonths}
                onChange={(months) => setNewForm({ ...newForm, activeMonths: months })}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setIsAdding(false);
                  setNewForm({
                    title: '',
                    description: '',
                    requiresConfirmation: false,
                    activeMonths: null
                  });
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Abbrechen
              </button>
              <button
                onClick={handleAdd}
                disabled={!newForm.title.trim()}
                style={{
                  padding: '8px 16px',
                  backgroundColor: newForm.title.trim() ? '#10B981' : '#9CA3AF',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: newForm.title.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '14px'
                }}
              >
                Hinzufügen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* To-Dos Liste */}
      {todos.length === 0 ? (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          backgroundColor: '#F9FAFB',
          borderRadius: '8px',
          border: '1px solid #E5E7EB'
        }}>
          <CheckSquare size={48} color="#9CA3AF" style={{ marginBottom: '12px' }} />
          <p style={{ color: '#6B7280', margin: 0 }}>
            Keine individuellen To-Dos definiert
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {todos.map((todo, index) => (
            <div
              key={todo.id}
              style={{
                padding: '12px',
                backgroundColor: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}
            >
              {/* Drag Handle */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '4px',
                paddingTop: '4px'
              }}>
                <button
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                  style={{
                    padding: '2px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: index === 0 ? 'not-allowed' : 'pointer',
                    opacity: index === 0 ? 0.3 : 1
                  }}
                >
                  ↑
                </button>
                <GripVertical size={16} color="#9CA3AF" />
                <button
                  onClick={() => handleMoveDown(index)}
                  disabled={index === todos.length - 1}
                  style={{
                    padding: '2px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: index === todos.length - 1 ? 'not-allowed' : 'pointer',
                    opacity: index === todos.length - 1 ? 0.3 : 1
                  }}
                >
                  ↓
                </button>
              </div>

              {/* Content */}
              <div style={{ flex: 1 }}>
                {editingId === todo.id ? (
                  <div style={{ display: 'grid', gap: '8px' }}>
                    <input
                      type="text"
                      value={editForm.title}
                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                      placeholder="Titel"
                      style={{
                        padding: '6px 10px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                    <textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      placeholder="Beschreibung"
                      rows={2}
                      style={{
                        padding: '6px 10px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '4px',
                        fontSize: '14px',
                        resize: 'vertical'
                      }}
                    />
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      fontSize: '13px'
                    }}>
                      <input
                        type="checkbox"
                        checked={editForm.requiresConfirmation}
                        onChange={(e) => setEditForm({ ...editForm, requiresConfirmation: e.target.checked })}
                      />
                      <ShieldCheck size={14} color={editForm.requiresConfirmation ? '#10B981' : '#9CA3AF'} />
                      Bestätigung erforderlich
                    </label>
                    <MonthSelector 
                      selectedMonths={editForm.activeMonths}
                      onChange={(months) => setEditForm({ ...editForm, activeMonths: months })}
                    />
                  </div>
                ) : (
                  <>
                    <div style={{ 
                      fontWeight: '500', 
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <CheckSquare size={16} color="#6B7280" />
                      {todo.title}
                      {todo.requires_confirmation && (
                        <span 
                          title="Bestätigung erforderlich"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '2px 6px',
                            backgroundColor: '#FEF3C7',
                            color: '#92400E',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600'
                          }}
                        >
                          <ShieldCheck size={12} />
                          BESTÄTIGUNG
                        </span>
                      )}
                      {todo.active_months && (
                        <span 
                          title={getMonthsDisplay(todo.active_months)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '2px 6px',
                            backgroundColor: '#E0E7FF',
                            color: '#3730A3',
                            borderRadius: '4px',
                            fontSize: '11px'
                          }}
                        >
                          <Calendar size={12} />
                          {getMonthsDisplay(todo.active_months)}
                        </span>
                      )}
                    </div>
                    {todo.description && (
                      <div style={{ 
                        fontSize: '13px', 
                        color: '#6B7280', 
                        marginTop: '4px',
                        marginLeft: '24px',
                        lineHeight: '1.4'
                      }}>
                        {todo.description}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '8px' }}>
                {editingId === todo.id ? (
                  <>
                    <button
                      onClick={() => handleSaveEdit(todo.id)}
                      disabled={!editForm.title.trim()}
                      style={{
                        padding: '6px',
                        backgroundColor: editForm.title.trim() ? '#10B981' : '#9CA3AF',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: editForm.title.trim() ? 'pointer' : 'not-allowed'
                      }}
                    >
                      <Save size={16} />
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      style={{
                        padding: '6px',
                        backgroundColor: '#F3F4F6',
                        color: '#374151',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      <X size={16} />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => handleEdit(todo)}
                      style={{
                        padding: '6px',
                        backgroundColor: '#F3F4F6',
                        color: '#374151',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(todo.id)}
                      style={{
                        padding: '6px',
                        backgroundColor: '#FEE2E2',
                        color: '#991B1B',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};